import React, { useState } from 'react';
import { motion } from 'framer-motion';
import PageSEO from '../components/PageSEO';
import {
  User, Activity, Cigarette, Scale, Dumbbell, MapPin, AlertTriangle,
  CheckCircle, TrendingUp, Shield, ChevronRight, Heart, Info, Zap,
  Moon, Coffee, Users, Apple, Wine, Briefcase, CreditCard
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
import { fetchWithFallback } from '../lib/dataFetcher';

function calculateRiskClientSide(form) {
  let base_score = 50;
  const age = parseInt(form.age) || 35;
  const gender = form.gender || 'female';
  const smoking = form.smoking === true || form.smoking === 'true';
  const bmi = parseFloat(form.bmi) || 25;
  const activity = form.activity || 'moderate';
  const race = form.race || 'not_specified';
  const family_history = form.familyHistory === true || form.familyHistory === 'true';
  const diet = form.diet || 'average';
  const sleep = form.sleep || 'adequate';
  const stress = form.stress || 'moderate';
  const alcohol = form.alcohol || 'moderate';
  const insurance = form.insurance || 'insured';
  const income = form.income || 'middle';

  if (age < 35) base_score -= 10;
  else if (age > 55) base_score += 15;
  else if (age > 45) base_score += 5;
  if (gender === 'male') base_score += 5;
  if (smoking) base_score += 25;
  if (bmi >= 30) base_score += 15;
  else if (bmi >= 25) base_score += 8;
  else if (bmi < 18.5) base_score += 5;
  if (activity === 'sedentary') base_score += 15;
  else if (activity === 'active') base_score -= 12;
  if (family_history) base_score += 10;
  if (diet === 'poor') base_score += 12;
  else if (diet === 'healthy') base_score -= 10;
  if (sleep === 'poor') base_score += 10;
  else if (sleep === 'good') base_score -= 5;
  if (stress === 'high') base_score += 12;
  else if (stress === 'low') base_score -= 5;
  if (alcohol === 'heavy') base_score += 15;
  else if (alcohol === 'none') base_score -= 3;
  if (insurance === 'uninsured') base_score += 8;
  if (income === 'low') base_score += 8;
  else if (income === 'high') base_score -= 4;
  const raceAdj = { black: 5, native: 5, hispanic: 2, asian: -2, pacific: 3 };
  base_score += raceAdj[race] || 0;

  const risk_score = Math.max(10, Math.min(95, base_score));
  let risk_level, risk_color;
  if (risk_score >= 70) { risk_level = 'high'; risk_color = '#ef4444'; }
  else if (risk_score >= 40) { risk_level = 'moderate'; risk_color = '#f59e0b'; }
  else { risk_level = 'low'; risk_color = '#10b981'; }

  const insights = [];
  if (smoking) insights.push({ factor: 'Smoking', impact: 'high', message: 'Smoking is your #1 risk driver. Quitting reduces heart disease risk by 50% within 1 year.' });
  if (bmi >= 30) insights.push({ factor: 'BMI', impact: 'high', message: 'Your BMI is 30+. Even modest lifestyle changes can improve metabolic health.' });
  else if (bmi >= 25) insights.push({ factor: 'BMI', impact: 'medium', message: 'Your BMI is in the overweight range. Focus on healthy behaviors rather than the number.' });
  if (activity === 'sedentary') insights.push({ factor: 'Activity', impact: 'high', message: 'Sedentary lifestyle increases all-cause mortality by 30%. Start with just 10 minutes of walking.' });
  if (family_history) insights.push({ factor: 'Family History', impact: 'medium', message: 'Family history increases risk but healthy lifestyle can offset much of the genetic risk.' });
  if (diet === 'poor') insights.push({ factor: 'Diet', impact: 'high', message: 'Poor diet quality is a major risk factor. Small changes like adding vegetables can make a big difference.' });
  if (sleep === 'poor') insights.push({ factor: 'Sleep', impact: 'medium', message: 'Poor sleep increases inflammation and chronic disease risk. Aim for 7-9 hours.' });
  if (stress === 'high') insights.push({ factor: 'Stress', impact: 'medium', message: 'Chronic stress affects immune function and cardiovascular health. Consider stress management techniques.' });
  if (alcohol === 'heavy') insights.push({ factor: 'Alcohol', impact: 'high', message: 'Heavy drinking increases risk of liver disease, several cancers, and weakens the immune system.' });
  if (insurance === 'uninsured') insights.push({ factor: 'Insurance', impact: 'medium', message: 'Uninsured individuals are less likely to receive preventive care. Explore options at healthcare.gov.' });
  if (income === 'low') insights.push({ factor: 'Income & Access', impact: 'medium', message: 'Lower income is associated with reduced access to healthy food and quality healthcare.' });
  if (['black', 'native', 'hispanic'].includes(race)) insights.push({ factor: 'Health Equity', impact: 'medium', message: 'Health disparities exist due to systemic factors — not biological differences. Advocate for equitable care.' });
  if (age > 55) insights.push({ factor: 'Age', impact: 'medium', message: 'Age is non-modifiable, but lifestyle changes at ANY age still reduce risk significantly.' });
  if (age > 45 && gender === 'male') insights.push({ factor: 'Gender + Age', impact: 'medium', message: 'Men over 45 have elevated cardiovascular risk. Regular screenings are especially important.' });
  if (!insights.length) insights.push({ factor: 'Overall', impact: 'low', message: "You're doing well! Maintain your healthy habits." });

  return {
    risk_score, risk_level, risk_color, us_average: 50,
    comparison: risk_score > 50 ? 'higher' : risk_score < 50 ? 'lower' : 'average',
    insights,
    top_risk_factor: insights[0]?.factor || 'None',
    autoimmune_note: 'Autoimmune diseases affect ~8% of the population. Risk factors overlap with chronic disease.',
    bmi_disclaimer: "BMI has known limitations: it doesn't distinguish muscle from fat and varies by ethnicity.",
    recommendations: [
      'Get regular health screenings appropriate for your age',
      'Maintain a balanced, anti-inflammatory diet (Mediterranean-style)',
      'Aim for 150-300 minutes of moderate activity per week',
      'Prioritize quality sleep (7-9 hours for adults)',
      'Manage stress through mindfulness, exercise, and social connection',
      'Stay up-to-date on vaccinations',
      'Know your family history'
    ]
  };
}
const STATES = ["US","AL","AK","AZ","AR","CA","CO","CT","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

const BMI_DISCLAIMER = "BMI has limitations: it doesn't distinguish muscle from fat, varies by ethnicity, and doesn't measure fat distribution. It's a screening tool, not a diagnosis. Athletes may show 'overweight' despite being healthy.";

const RiskGauge = ({ score, color }) => {
  const angle = (score / 100) * 180;
  return (
    <div className="relative w-56 h-32 mx-auto" data-testid="risk-gauge">
      <svg viewBox="0 0 200 110" className="w-full">
        <path d="M 10 100 A 90 90 0 0 1 70 18" fill="none" stroke="#10b981" strokeWidth="14" strokeLinecap="round" opacity="0.25" />
        <path d="M 70 18 A 90 90 0 0 1 130 18" fill="none" stroke="#f59e0b" strokeWidth="14" opacity="0.25" />
        <path d="M 130 18 A 90 90 0 0 1 190 100" fill="none" stroke="#ef4444" strokeWidth="14" strokeLinecap="round" opacity="0.25" />
        <line x1="100" y1="100" x2={100 + 70 * Math.cos(Math.PI - (angle * Math.PI / 180))} y2={100 - 70 * Math.sin(Math.PI - (angle * Math.PI / 180))} stroke={color} strokeWidth="3" strokeLinecap="round" />
        <circle cx="100" cy="100" r="6" fill={color} />
        <text x="15" y="108" fontSize="10" fill="#94a3b8">Low</text>
        <text x="88" y="15" fontSize="10" fill="#94a3b8">Med</text>
        <text x="170" y="108" fontSize="10" fill="#94a3b8">High</text>
      </svg>
      <div className="absolute bottom-0 left-0 right-0 text-center">
        <p className="text-4xl font-bold" style={{ color }}>{score}</p>
        <p className="text-xs text-slate-500">out of 100</p>
      </div>
    </div>
  );
};

const InsightCard = ({ insight }) => {
  const colors = {
    high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: AlertTriangle },
    medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: TrendingUp },
    low: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: CheckCircle },
  };
  const c = colors[insight.impact] || colors.low;
  const Icon = c.icon;
  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4 flex items-start gap-3`} data-testid={`insight-${insight.factor.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon className={`w-5 h-5 ${c.text} shrink-0 mt-0.5`} />
      <div>
        <p className={`text-sm font-semibold ${c.text}`}>{insight.factor}</p>
        <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">{insight.message}</p>
      </div>
    </div>
  );
};

export default function YouInTheData() {
  const [form, setForm] = useState({ 
    age: 35, 
    gender: 'male', 
    state: 'US', 
    smoking: false, 
    bmi: 25, 
    activity: 'moderate',
    race: 'not_specified',
    familyHistory: false,
    diet: 'average',
    sleep: 'adequate',
    stress: 'moderate',
    alcohol: 'moderate',
    insurance: 'insured',
    income: 'middle'
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleCalculate = () => {
    setLoading(true);
    const params = new URLSearchParams({ 
      age: form.age, 
      gender: form.gender, 
      state: form.state, 
      smoking: form.smoking, 
      bmi: form.bmi, 
      activity: form.activity,
      race: form.race,
      family_history: form.familyHistory,
      diet: form.diet,
      sleep: form.sleep,
      stress: form.stress,
      alcohol: form.alcohol,
      insurance: form.insurance,
      income: form.income
    });
    setResult(calculateRiskClientSide(form));
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="you-in-the-data-page">
      <PageSEO title="Risk Calculator" description="Interactive health risk calculator based on 14 factors including age, BMI, smoking, diet, and more. See how your profile compares to national averages using CDC data." path="/risk" />
      {/* Header */}
      <section className="relative bg-white border-b border-slate-200/60 overflow-hidden">
        <div className="absolute right-0 top-0 w-1/3 h-full opacity-10">
          <img src="https://images.unsplash.com/photo-1603206004639-22635b71ac08?w=600&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center"><User className="w-5 h-5 text-white" /></div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">You in the Data</h1>
            </div>
            <p className="text-base text-slate-500 max-w-2xl">Enter your profile and see how you compare to national health data. Get personalized insights on chronic and autoimmune disease risk factors.</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm sticky top-24" data-testid="risk-form">
              <h2 className="text-lg font-semibold text-slate-900 mb-5 flex items-center gap-2"><Activity className="w-5 h-5 text-teal-600" /> Your Profile</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Age</label>
                  <input type="number" min="18" max="100" value={form.age} onChange={(e) => setForm({ ...form, age: parseInt(e.target.value) || 18 })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm" data-testid="input-age" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Gender</label>
                  <div className="flex gap-2" data-testid="input-gender">
                    {['male', 'female'].map(g => (
                      <button key={g} onClick={() => setForm({ ...form, gender: g })}
                        className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${form.gender === g ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><MapPin className="w-4 h-4" /> State</label>
                  <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm bg-white" data-testid="input-state">
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Cigarette className="w-4 h-4" /> Current Smoker</label>
                  <button onClick={() => setForm({ ...form, smoking: !form.smoking })} data-testid="input-smoking"
                    className={`w-12 h-7 rounded-full transition-all relative ${form.smoking ? 'bg-red-500' : 'bg-slate-200'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${form.smoking ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Scale className="w-4 h-4" /> BMI: <strong className="text-teal-600">{form.bmi}</strong></label>
                  <input type="range" min="15" max="50" step="0.5" value={form.bmi} onChange={(e) => setForm({ ...form, bmi: parseFloat(e.target.value) })}
                    className="w-full accent-teal-600" data-testid="input-bmi" />
                  <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                    <span>15 (Under)</span><span>18.5-24.9 (Normal)</span><span>30+ (Obese)</span><span>50</span>
                  </div>
                  <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-[10px] text-amber-700 flex items-start gap-1">
                      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                      {BMI_DISCLAIMER}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Dumbbell className="w-4 h-4" /> Activity Level</label>
                  <div className="grid grid-cols-3 gap-2" data-testid="input-activity">
                    {['sedentary', 'moderate', 'active'].map(a => (
                      <button key={a} onClick={() => setForm({ ...form, activity: a })}
                        className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.activity === a ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                        {a.charAt(0).toUpperCase() + a.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Advanced Filters Toggle */}
                <button 
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="w-full py-2 text-sm text-teal-600 font-medium flex items-center justify-center gap-1 hover:text-teal-700 transition-colors"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
                  {showAdvanced ? 'Hide' : 'Show'} Advanced Filters
                </button>

                {/* Advanced Filters */}
                {showAdvanced && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-4 pt-2 border-t border-slate-100"
                  >
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Users className="w-4 h-4" /> Race/Ethnicity</label>
                      <select value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })}
                        className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all text-sm bg-white">
                        <option value="not_specified">Prefer not to say</option>
                        <option value="white">White</option>
                        <option value="black">Black or African American</option>
                        <option value="hispanic">Hispanic or Latino</option>
                        <option value="asian">Asian</option>
                        <option value="native">American Indian/Alaska Native</option>
                        <option value="pacific">Native Hawaiian/Pacific Islander</option>
                        <option value="multiracial">Two or More Races</option>
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Health disparities exist across racial groups due to systemic factors, not biology.</p>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700 flex items-center gap-1.5"><Heart className="w-4 h-4" /> Family History of Chronic Disease</label>
                      <button onClick={() => setForm({ ...form, familyHistory: !form.familyHistory })}
                        className={`w-12 h-7 rounded-full transition-all relative ${form.familyHistory ? 'bg-amber-500' : 'bg-slate-200'}`}>
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-all shadow-sm ${form.familyHistory ? 'left-6' : 'left-1'}`} />
                      </button>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Apple className="w-4 h-4" /> Diet Quality</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['poor', 'average', 'healthy'].map(d => (
                          <button key={d} onClick={() => setForm({ ...form, diet: d })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.diet === d ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {d.charAt(0).toUpperCase() + d.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Moon className="w-4 h-4" /> Sleep Quality</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['poor', 'adequate', 'good'].map(s => (
                          <button key={s} onClick={() => setForm({ ...form, sleep: s })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.sleep === s ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {s.charAt(0).toUpperCase() + s.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Coffee className="w-4 h-4" /> Stress Level</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['low', 'moderate', 'high'].map(st => (
                          <button key={st} onClick={() => setForm({ ...form, stress: st })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.stress === st ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {st.charAt(0).toUpperCase() + st.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Wine className="w-4 h-4" /> Alcohol Consumption</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['none', 'moderate', 'heavy'].map(a => (
                          <button key={a} onClick={() => setForm({ ...form, alcohol: a })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.alcohol === a ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {a.charAt(0).toUpperCase() + a.slice(1)}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Heavy = 14+ drinks/week (men) or 7+ (women)</p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><CreditCard className="w-4 h-4" /> Insurance Status</label>
                      <div className="grid grid-cols-2 gap-2">
                        {['insured', 'uninsured'].map(ins => (
                          <button key={ins} onClick={() => setForm({ ...form, insurance: ins })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.insurance === ins ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {ins.charAt(0).toUpperCase() + ins.slice(1)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1.5 flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> Household Income</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['low', 'middle', 'high'].map(inc => (
                          <button key={inc} onClick={() => setForm({ ...form, income: inc })}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${form.income === inc ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                            {inc.charAt(0).toUpperCase() + inc.slice(1)}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-slate-400 mt-1">Income affects access to healthy food, exercise, and healthcare.</p>
                    </div>
                  </motion.div>
                )}

                <button onClick={handleCalculate} disabled={loading} data-testid="calculate-risk-btn"
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-semibold text-sm hover:from-teal-700 hover:to-emerald-700 transition-all shadow-sm disabled:opacity-60 flex items-center justify-center gap-2">
                  {loading ? 'Calculating...' : <><ChevronRight className="w-4 h-4" /> Calculate My Risk</>}
                </button>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-3">
            {!result ? (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-slate-200/60 p-10 shadow-sm text-center" data-testid="risk-placeholder">
                  <Heart className="w-16 h-16 mx-auto text-slate-200 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-400 mb-2">Enter your profile</h3>
                  <p className="text-sm text-slate-400">Fill in the form and click "Calculate My Risk" to see your personalized insights.</p>
                </div>
                {/* Educational content while waiting */}
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2"><Info className="w-4 h-4" /> Did You Know?</h3>
                  <ul className="space-y-2 text-sm text-amber-700">
                    <li className="flex items-start gap-2"><Zap className="w-4 h-4 shrink-0 mt-0.5" /> 80% of chronic diseases are preventable through lifestyle changes</li>
                    <li className="flex items-start gap-2"><Zap className="w-4 h-4 shrink-0 mt-0.5" /> Smoking increases risk of 10+ autoimmune diseases including lupus and Crohn's</li>
                    <li className="flex items-start gap-2"><Zap className="w-4 h-4 shrink-0 mt-0.5" /> Just 150 minutes of walking per week reduces heart disease risk by 30-40%</li>
                    <li className="flex items-start gap-2"><Zap className="w-4 h-4 shrink-0 mt-0.5" /> Your state of residence significantly affects health outcomes — a 10% gap exists between the healthiest and unhealthiest states</li>
                  </ul>
                </div>
              </div>
            ) : (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="risk-results">
                {/* Score */}
                <div className="bg-white rounded-2xl border border-slate-200/60 p-8 shadow-sm text-center">
                  <h2 className="text-lg font-semibold text-slate-900 mb-6">Your Risk Score</h2>
                  <RiskGauge score={result.risk_score} color={result.risk_color} />
                  <div className="mt-6 flex items-center justify-center gap-3">
                    <span className={`text-sm font-bold px-4 py-1.5 rounded-full ${result.risk_level === 'high' ? 'bg-red-100 text-red-700' : result.risk_level === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`} data-testid="risk-level-badge">
                      {result.risk_level.toUpperCase()} RISK
                    </span>
                    <span className="text-sm text-slate-500">
                      {result.comparison === 'higher' ? 'Above' : result.comparison === 'lower' ? 'Below' : 'At'} US average
                    </span>
                  </div>
                </div>

                {/* Comparison Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="comparison-bar">
                  <h3 className="text-sm font-semibold text-slate-900 mb-3">You vs US Average</h3>
                  <div className="relative h-10 bg-slate-100 rounded-full overflow-hidden">
                    <div className="absolute h-full rounded-full bg-slate-200" style={{ width: `${result.us_average}%` }} />
                    <div className="absolute h-full rounded-full transition-all duration-700" style={{ width: `${result.risk_score}%`, backgroundColor: result.risk_color, opacity: 0.7 }} />
                    <div className="absolute h-full w-0.5 bg-slate-400" style={{ left: `${result.us_average}%` }} />
                  </div>
                  <div className="flex justify-between text-xs text-slate-500 mt-2">
                    <span>0</span>
                    <div className="flex gap-6">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300" /> US Avg ({result.us_average})</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: result.risk_color }} /> You ({result.risk_score})</span>
                    </div>
                    <span>100</span>
                  </div>
                </div>

                {/* State info */}
                {result.state_info && (
                  <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5" data-testid="state-info">
                    <h3 className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-2"><MapPin className="w-4 h-4" /> {result.state_info.state} Health Index</h3>
                    <p className="text-sm text-blue-700">Your state has a health index of <strong>{result.state_info.state_health_index}</strong> — {result.state_info.comparison} compared to other states.</p>
                  </div>
                )}

                {/* Insights */}
                <div className="space-y-3" data-testid="risk-insights">
                  <h3 className="text-sm font-semibold text-slate-900">Key Insights</h3>
                  {result.insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
                </div>

                {/* Autoimmune note */}
                {result.autoimmune_note && (
                  <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5" data-testid="autoimmune-note">
                    <h3 className="text-sm font-semibold text-purple-800 mb-1 flex items-center gap-2"><Shield className="w-4 h-4" /> Autoimmune Connection</h3>
                    <p className="text-sm text-purple-700">{result.autoimmune_note}</p>
                  </div>
                )}

                {/* Recommendations */}
                <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6" data-testid="recommendations">
                  <h3 className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2"><Shield className="w-4 h-4" /> Recommendations</h3>
                  <ul className="space-y-2">
                    {result.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-teal-700">
                        <CheckCircle className="w-4 h-4 text-teal-500 shrink-0 mt-0.5" /> {rec}
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-xs text-slate-400 text-center">This is for educational purposes only and is not medical advice. Consult your healthcare provider.</p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
